import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Navigate, Link, useLocation } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import { useOktaAuth, withOktaAuth } from '@okta/okta-react'
import { toRelativeUrl } from '@okta/okta-auth-js'

import { Message } from './Message'
import { USER_LOGGED_IN } from '../../constants/user'
import routes from '../../constants/routes'
import { isAuthenticated as isAuthenticatedSelector, isLoading as isLoadingSelector } from '../selectors'

import './auth.scss'
import Spinner from '../../../app/components/shared/Spinner'

const InlineSpinner = ({ size = 20, color = "#fff" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 44 44"
    xmlns="http://www.w3.org/2000/svg"
    stroke={color}
    style={{ verticalAlign: 'middle', marginRight: '8px' }}
  >
    <g fill="none" fillRule="evenodd" strokeWidth="4">
      <circle cx="22" cy="22" r="20" strokeOpacity=".3" />
      <path d="M42 22c0-11.046-8.954-20-20-20">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 22 22"
          to="360 22 22"
          dur="0.8s"
          repeatCount="indefinite" />
      </path>
    </g>
  </svg>
);

const NewLogin = ({ authState, isAuthenticated, isLoading, dispatch }) => {
  const location = useLocation()
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const { oktaAuth } = useOktaAuth()

  const redirect = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return params.get('redirect');
  }, [location.search]);

  const auth = useMemo(() => authState?.isAuthenticated === true, [authState]);

  const signInWithOkta = async () => {
    setIsLoggingIn(true);

    try {
      const originalUri = toRelativeUrl(
        `${window.location.origin}/submissions`,
        window.location.origin,
      );

      oktaAuth.setOriginalUri(originalUri);

      await oktaAuth.signInWithRedirect();
    } catch (err) {
      console.error('Okta Login Error:', err);
      setIsLoggingIn(false);
    }
  };

  useEffect(() => {
    if (auth && !isAuthenticated && authState?.user) {
      dispatch({
        type: USER_LOGGED_IN,
        payload: authState.user,
      });
    }
  }, [auth, isAuthenticated, authState?.user, dispatch]);

  const renderInvalidInviteTokenMessage = () => {
    return (
      <Message title={'You have selected an invalid link'} type="info">
        The link you have selected is inactive, please check your email for a more recent
        version or if you have signed up, log in below
      </Message>
    )
  }

  const { state } = location

  if (isLoading || (auth && !isAuthenticated)) {
    return <Spinner />
  }

  if (isAuthenticated) {
    return <Navigate to={redirect || routes.SUBMISSIONS} replace />
  }

  return (
    <div className="AuthContainer container vertical">
      {state && state.from === 'inviteToken' && renderInvalidInviteTokenMessage()}
      <div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginTop: 32,
            gap: 16,
          }}
        >
          {OE_ENV.BLOCK_BROKER_ACCESS_NOTIFICATION === 'true' && <Message title={`Owner's Edge Access Notification`} type="warning">
            Please be aware, the Owner's Edge application no longer supports access for Agent Users. 
            Current usernames and passwords have been disabled for Owner's Edge. Please send submissions 
            to: <a style={{fontSize: '1em'}} href={`mailto:${OE_ENV.SUBMISSIONS_EMAIL_ADDRESS}`}>{OE_ENV.SUBMISSIONS_EMAIL_ADDRESS}</a>
          </Message>}
          <h1 style={{ margin: 0 }}>Login</h1>
          <p style={{ margin: 0, fontSize: '1.2em' }}>
            To an <strong>existing</strong> Owner&apos;s Edge account
          </p>
          <Button
            id="login-button"
            type="submit"
            className="login"
            onClick={signInWithOkta}
            disabled={isLoggingIn}
            style={{ width: 100 }}
          >
            {isLoggingIn ? <InlineSpinner /> : 'Login'}
          </Button>
          <p
            className="col-xs-12 trouble_login"
            style={{ padding: 0, margin: 0, fontSize: '1.2em' }}
          >
            <Link to="/logintrouble" className="btn-link">
              Trouble logging in?
            </Link>
          </p>
        </div>
        <Link
          to="/signup"
          className="btn btn-link"
          style={{ paddingLeft: 0, fontSize: '1.2em' }}
        >
          Don’t have an account? Sign-up here
        </Link>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticatedSelector(state),
  isLoading: isLoadingSelector(state),
})

export default withOktaAuth(connect(mapStateToProps)(NewLogin))
